import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'

/**
 * `full` — tier accordion / standard benefit lists (quantity in the title).
 * `compact` — Société tier-upgrade toast when the tier badge shows quantity (no duplicate prefix in the title).
 */
export type TierProgressionBenefitTitleVariant = 'full' | 'compact'

function renderTierProgressionBenefitTitle(
  benefit: App.LoyaltyRegionTierBenefit,
  variant: TierProgressionBenefitTitleVariant,
): React.ReactElement {
  const { code, limit, unlimited, value } = benefit
  const count = limit ?? 1
  const compact = variant === 'compact'

  switch (code) {
    case 'hotel_upgrade':
      if (unlimited) {
        if (compact) {
          return (
            <FormattedMessage
              id="S8ekbrIsA6ZW4mBk028q"
              defaultMessage="Free room or package upgrades"
              description="Benefit title without quantity when amount is shown in a badge"
            />
          )
        }
        return (
          <FormattedMessage
            id="IaFiELEGqzLc2O8xNkZe"
            defaultMessage="Unlimited free room or package upgrades"
            description="Benefit title for unlimited room upgrades on the room page"
          />
        )
      }
      if (compact) {
        return (
          <FormattedMessage
            id="933YW1IMH4MCXDvRY2Wh"
            defaultMessage="{count, plural, one {Free room or package upgrade} other {Free room or package upgrades}}"
            description="Benefit title without leading count when amount is shown in a badge"
            values={{ count }}
          />
        )
      }
      return (
        <FormattedMessage
          id="yC0WsNLD_Ob456YVXBe8"
          defaultMessage="{count, plural, one {1x Free room or package upgrade} other {#x Free room or package upgrades}}"
          description="Benefit title for limited room upgrades on the hotel or room booking page"
          values={{ count }}
        />
      )
    case 'airport_transfer':
      if (compact) {
        return (
          <FormattedMessage
            id="1jI7_JmKE2UoR8Q86FTs"
            defaultMessage="Free airport chauffeur service"
            description="Benefit title without quantity when amount is shown in a badge"
          />
        )
      }
      if (unlimited) {
        return (
          <FormattedMessage
            id="yKDIK0jxc6_Swu5f_nwH"
            defaultMessage="Unlimited free airport chauffeur service"
            description="Benefit title for unlimited airport transfers in the loyalty program section"
          />
        )
      }
      return (
        <FormattedMessage
          id="My3cDInErtzR_fOMQ4On"
          defaultMessage="{count, plural, one {Free airport chauffeur service} other {#x Free airport chauffeur service}}"
          description="Benefit title for limited airport transfers for transfers"
          values={{ count }}
        />
      )
    case 'lounge_pass':
      if (unlimited) {
        if (compact) {
          return (
            <FormattedMessage
              id="rx59J4IepmNgVn-3eYmC"
              defaultMessage="Free airport lounge passes"
              description="Benefit title without quantity when amount is shown in a badge"
            />
          )
        }
        return (
          <FormattedMessage
            id="dHrlSrs_eiKFyqKId2t6"
            defaultMessage="Unlimited free airport lounge passes"
            description="Benefit title for unlimited lounge passes in the benefits list"
          />
        )
      }
      if (compact) {
        return (
          <FormattedMessage
            id="x83a1TLH2w6vsH9zUFkq"
            defaultMessage="{count, plural, one {Free airport lounge pass} other {Free airport lounge passes}}"
            description="Benefit title without leading count when amount is shown in a badge"
            values={{ count }}
          />
        )
      }
      return (
        <FormattedMessage
          id="Hjj8Q_UvNoaV5TYidvQE"
          defaultMessage="{count, plural, one {Free airport lounge pass} other {#x Free airport lounge passes}}"
          description="Benefit title for limited lounge passes in the loyalty benefits section"
          values={{ count }}
        />
      )
    case 'discounted_insurance':
      if (compact) {
        return (
          <FormattedMessage
            id="kWc1dKCrYz28MNkypzUd"
            defaultMessage="Travel protection discount"
            description="Benefit title without percent when the discount amount is shown in a badge"
          />
        )
      }
      return (
        <FormattedMessage
          id="HFVzqpSMkiuiw9upEzVF"
          defaultMessage="{percent}% Travel protection discount"
          description="Benefit title for travel insurance discount in the insurance flow"
          values={{ percent: value?.amount ?? 0 }}
        />
      )
    case 'phone_support':
      return (
        <FormattedMessage
          id="6VsYcSQ0vwKmoHOqfYpH"
          defaultMessage="Priority phone support 24/7"
          description="Benefit title for priority phone support in the support area"
        />
      )
    case 'tour_exp_credit':
      if (compact) {
        return (
          <FormattedMessage
            id="IV_XOMd6G49VPOMHZgyK"
            defaultMessage="{count, plural, one {<currency></currency> Tour optional extra credit} other {<currency></currency> Tour optional extra credits}}"
            description="Tour credit title without leading count when amount is shown in a badge"
            values={{
              count,
              currency: () => <FormatCurrency value={value?.amount ?? 0} />,
            }}
          />
        )
      }
      return (
        <FormattedMessage
          id="n92hY4GKhaKiBWL83Fwu"
          defaultMessage="{count, plural, one {<currency></currency> Tour optional extra credit} other {#x <currency></currency> Tour optional extra credits}}"
          description="Benefit title for tour experience credits on the tour page"
          values={{
            count,
            currency: () => <FormatCurrency value={value?.amount ?? 0} />,
          }}
        />
      )
    case 'travelshoot_credit':
      return (
        <FormattedMessage
          id="Tjg9BJ96AVsv3DsZy8Di"
          defaultMessage="<currency></currency> TravelShoot voucher"
          description="Benefit title for TravelShoot voucher for promotional codes or vouchers"
          values={{
            currency: () => <FormatCurrency value={value?.amount ?? 0} />,
          }}
        />
      )
    case 'esim_package':
      return (
        <FormattedMessage
          id="votTmCrdVG_I5jd5Ohcp"
          defaultMessage="{value}{unit} eSIM per international trip"
          description="Benefit title for free eSIM package that provides global data connectivity for travelers"
          values={{
            value: value?.amount ?? 0,
            unit: value?.unit ?? 'GB',
          }}
        />
      )
    default:
      return <>{code}</>
  }
}

interface Props {
  benefit: App.LoyaltyRegionTierBenefit
  /** `full` (default) — quantity in the title. `compact` — omit duplicate quantity when shown in a badge (e.g. Société tier-upgrade toast). */
  variant?: TierProgressionBenefitTitleVariant
}

function TierProgressionBenefitTitle({ benefit, variant = 'full' }: Props): React.ReactElement {
  return renderTierProgressionBenefitTitle(benefit, variant)
}

export default TierProgressionBenefitTitle
